import { useRef } from 'react'
import StatusBadge from '../StatusBadge'
import type { TechnologyStatus } from '@/types/technology'
import type { Answers, EnquiryPayload, Question } from './enquiryModel'

interface Props {
  q: Question
  answers: Answers
  payload: EnquiryPayload
  error?: string
  files: File[]
  onChange: (key: string, value: unknown) => void
  onFiles: (files: File[]) => void
}

const input =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-[#ff6a1a]/60 focus:outline-none'
const choice = 'flex cursor-pointer items-start gap-3 rounded-lg border border-white/10 px-3 py-2.5 text-sm text-white/80'

/* One field per registry kind. A choice is shown by shade (wb-chosen), never by
 * the accent — CLAUDE.md, "A choice is a change of shade". */
export default function QuestionField({ q, answers, payload, error, files, onChange, onFiles }: Props) {
  const id = `enq-${q.key}`
  const labelId = `${id}-label`
  const value = answers[q.key]
  const invalid = error ? true : undefined
  const describedBy = [q.help ? `${id}-help` : null, error ? `${id}-error` : null].filter(Boolean).join(' ') || undefined
  const named = { 'aria-labelledby': labelId, 'aria-describedby': describedBy, 'aria-invalid': invalid }

  return (
    <fieldset className="space-y-2">
      <legend id={labelId} className="text-sm font-medium text-white">
        {q.label}
        {q.required ? <span className="text-[#ff8c4d]"> *</span> : <span className="text-white/40"> (optional)</span>}
      </legend>
      {q.help && <p id={`${id}-help`} className="text-xs text-white/50">{q.help}</p>}
      {renderControl()}
      {error && <p id={`${id}-error`} role="alert" className="text-xs text-red-400">{error}</p>}
    </fieldset>
  )

  function renderControl() {
    switch (q.kind) {
      case 'textarea':
        return <textarea id={id} {...named} className={`${input} min-h-24`} value={String(value ?? '')} onChange={e => onChange(q.key, e.target.value)} />
      case 'email':
      case 'url':
      case 'text':
        return <input id={id} {...named} type={q.kind === 'text' ? 'text' : q.kind} className={input} value={String(value ?? '')} placeholder={q.kind === 'url' ? 'https://' : undefined} onChange={e => onChange(q.key, e.target.value)} />
      case 'number':
        return <input id={id} {...named} type="number" min={0} className={`${input} max-w-40`} value={String(value ?? '')} onChange={e => onChange(q.key, e.target.value)} />
      case 'country':
        return (
          <select id={id} {...named} className={input} value={String(value ?? '')} onChange={e => onChange(q.key, e.target.value)}>
            <option value="">Choose a country</option>
            {payload.countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
          </select>
        )
      case 'single':
        return (
          <div className="grid gap-2 sm:grid-cols-2" role="radiogroup" {...named}>
            {(q.options ?? []).map(o => (
              <label key={o.key} className={`${choice} ${value === o.key ? 'wb-chosen' : ''}`}>
                <input type="radio" name={id} className="mt-0.5" checked={value === o.key} onChange={() => onChange(q.key, o.key)} />
                <span>{o.label}</span>
              </label>
            ))}
          </div>
        )
      case 'multi': {
        const picked = (value as string[] | undefined) ?? []
        const toggle = (k: string) => onChange(q.key, picked.includes(k) ? picked.filter(p => p !== k) : [...picked, k])
        return (
          <div className="grid gap-2 sm:grid-cols-2" role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
            {(q.options ?? []).map(o => (
              <label key={o.key} className={`${choice} ${picked.includes(o.key) ? 'wb-chosen' : ''}`}>
                <input type="checkbox" className="mt-0.5" aria-invalid={invalid} checked={picked.includes(o.key)} onChange={() => toggle(o.key)} />
                <span className="flex-1">{o.label}</span>
                {o.status_label && o.status !== 'live' && (
                  <StatusBadge status={o.status as TechnologyStatus} label={o.status_label} />
                )}
              </label>
            ))}
          </div>
        )
      }
      case 'currency':
        return (
          <select id={id} {...named} className={input} value={String(value ?? '')} onChange={e => onChange(q.key, e.target.value)}>
            <option value="">Choose a currency</option>
            {(q.options ?? []).map(o => <option key={o.key} value={o.key}>{o.label}</option>)}
          </select>
        )
      case 'revenue_band':
        return (
          <select id={id} {...named} className={input} value={String(value ?? '')} onChange={e => onChange(q.key, e.target.value)}>
            <option value="">Choose a band</option>
            {payload.revenue_bands.map((label, i) => <option key={label} value={`band_${i + 1}`}>{label} a month</option>)}
          </select>
        )
      case 'statements': {
        const s = (value as { basis?: string; amounts?: string[] } | undefined) ?? {}
        const amounts = s.amounts ?? ['', '', '']
        const set = (next: Partial<{ basis: string; amounts: string[] }>) => onChange(q.key, { basis: s.basis ?? 'gross', amounts, ...next })
        return (
          <div className="space-y-2" role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
            <div className="grid grid-cols-3 gap-2">
              {[0, 1, 2].map(i => (
                <input key={i} aria-label={`Statement ${i + 1}`} aria-invalid={invalid} inputMode="decimal" className={input} value={amounts[i] ?? ''} placeholder={`Statement ${i + 1}`}
                  onChange={e => set({ amounts: amounts.map((a, j) => (j === i ? e.target.value : a)) })} />
              ))}
            </div>
            <div className="flex gap-4 text-sm text-white/70">
              {['gross', 'net'].map(b => (
                <label key={b} className="flex items-center gap-2">
                  <input type="radio" name={`${id}-basis`} checked={(s.basis ?? 'gross') === b} onChange={() => set({ basis: b })} />
                  {b === 'gross' ? 'Gross' : 'Net'}
                </label>
              ))}
            </div>
          </div>
        )
      }
      case 'files':
        return <FilePicker id={id} labelId={labelId} describedBy={describedBy} invalid={invalid} files={files} onFiles={onFiles} />
    }
  }
}

const MAX_FILES = 3

function size(bytes: number): string {
  return bytes >= 1024 * 1024 ? `${(bytes / (1024 * 1024)).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`
}

interface PickerProps {
  id: string
  labelId: string
  describedBy?: string
  invalid?: boolean
  files: File[]
  onFiles: (files: File[]) => void
}

/* A visually hidden file input driven by a real button, so it is clickable and
 * reachable by keyboard. The input's value is cleared after every pick, which is
 * what lets the same file be chosen again once it has been removed. */
function FilePicker({ id, labelId, describedBy, invalid, files, onFiles }: PickerProps) {
  const ref = useRef<HTMLInputElement>(null)
  const pick = (picked: File[]) => {
    const next = [...files]
    for (const f of picked) {
      if (next.length < MAX_FILES && !next.some(e => e.name === f.name && e.size === f.size)) next.push(f)
    }
    onFiles(next)
    if (ref.current) ref.current.value = ''
  }
  return (
    <div role="group" aria-labelledby={labelId} aria-describedby={describedBy} className="space-y-2">
      <input ref={ref} id={id} type="file" multiple accept=".csv,.xls,.xlsx" className="sr-only" tabIndex={-1} aria-hidden="true"
        onChange={e => pick(Array.from(e.target.files ?? []))} />
      <button type="button" aria-describedby={describedBy} aria-invalid={invalid} disabled={files.length >= MAX_FILES}
        className="marketing-button-secondary !px-4 !py-2 text-sm disabled:opacity-40"
        onClick={() => ref.current?.click()}>
        Choose files
      </button>
      {files.length > 0 && (
        <ul className="space-y-1" aria-label="Selected files">
          {files.map(f => (
            <li key={`${f.name}-${f.size}`} className="flex items-center justify-between gap-3 rounded-lg border border-white/10 px-3 py-1.5 text-xs text-white/70">
              <span className="truncate">{f.name} <span className="text-white/40">({size(f.size)})</span></span>
              <button type="button" aria-label={`Remove ${f.name}`} className="px-1 text-base leading-none text-white/60 hover:text-white focus:outline-none focus-visible:ring-1 focus-visible:ring-[#ff6a1a]"
                onClick={() => onFiles(files.filter(x => x !== f))}>×</button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
